// features/notifications/components/PushNotificationSettings.tsx
//
// The per-user notifications card shown on the Settings page. A single master
// toggle controls all RoundMaster push notifications for the signed-in user.
// What each role actually receives is decided server-side:
//   - Head Judges / Managers  -> new appeals
//   - All assigned staff      -> missing tables ("please go and check")
//   - Staff + following players-> next round paired
// so a player simply sees "round paired" while a Head Judge also gets appeals.

import { JSX, useState } from 'react';
import { Box, Typography, Switch, Divider, CircularProgress, Button } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SendIcon from '@mui/icons-material/Send';
import { GlassCard, CardContent } from '../../../components/common/Card';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useNotification } from '../../../context/NotificationContext';
import { getCallable } from '../../../services/firebaseFunctions';
import { getCurrentToken, showLocalTestNotification } from '../../../services/pushNotifications';

// iOS only supports Web Push from an installed (Add to Home Screen) PWA.
const isIos =
  typeof navigator !== 'undefined' &&
  /iP(hone|ad|od)/.test(navigator.userAgent) &&
  !(window as unknown as { MSStream?: unknown }).MSStream;

export function PushNotificationSettings(): JSX.Element {
  const { supported, permission, enabled, loading, busy, enable, disable } = usePushNotifications();
  const notify = useNotification();
  const [testing, setTesting] = useState(false);

  const handleToggle = (_e: unknown, checked: boolean) => {
    if (checked) enable();
    else disable();
  };

  // Server round-trip self-test, targeted at THIS device. Isolates the FCM
  // path for the current device from the recipient/trigger logic. Tip:
  // background the app to see the OS banner — when the tab is focused it
  // arrives as the in-app toast instead.
  const handleTest = async () => {
    setTesting(true);
    try {
      const token = await getCurrentToken();
      if (!token) {
        notify.warning('This device is not registered. Toggle notifications off and on here, then retry.');
        return;
      }
      const sendTest = await getCallable<{ token: string }, { sent: number; failed?: number; reason?: string }>(
        'sendTestPush',
      );
      const { data } = await sendTest({ token });
      if (data.sent > 0) {
        notify.success('Test sent to this device — background the app to see the banner.');
      } else if (data.reason === 'no-token-this-device') {
        notify.warning('This device’s token isn’t stored yet. Toggle off and on here, then retry.');
      } else if (data.reason === 'not-enabled') {
        notify.warning('No device token found. Toggle notifications off and on here, then retry.');
      } else {
        notify.error('FCM rejected this device’s token. Toggle off and on to refresh it.');
      }
    } catch (err) {
      notify.error('Could not reach the notification service. Are the Cloud Functions deployed?');
    } finally {
      setTesting(false);
    }
  };

  // Local display check: shows a notification straight from the service worker
  // (no server / no FCM). If THIS works but the server test doesn't, the
  // problem is FCM delivery; if neither works, it's permission/OS/PWA.
  const handleLocalTest = async () => {
    const ok = await showLocalTestNotification();
    if (!ok) {
      notify.error('This device could not display a notification (permission or platform issue).');
    }
  };

  const blocked = permission === 'denied';

  return (
    <GlassCard sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <NotificationsActiveIcon sx={{ color: '#00FFD4' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFF' }}>
            Notifications
          </Typography>
        </Box>

        <Typography sx={{ color: '#8892A0', mb: 2, fontSize: '0.875rem' }}>
          Get a push notification on this device for appeals, missing tables and new
          round pairings — even when the browser tab is in the background. You choose
          whether to allow it; you can turn it off any time.
        </Typography>

        {!supported ? (
          <Typography sx={{ color: '#FFAA00', fontSize: '0.875rem' }}>
            This browser does not support push notifications.
            {isIos && ' On iPhone/iPad, add RoundMaster to your Home Screen first, then open it from there.'}
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                backgroundColor: '#252D3A',
                borderRadius: '8px',
              }}
            >
              <Box>
                <Typography sx={{ color: '#FFF', fontWeight: 600 }}>
                  Push notifications
                </Typography>
                <Typography sx={{ color: '#8892A0', fontSize: '0.8rem' }}>
                  {enabled ? 'On for this device' : 'Off'}
                </Typography>
              </Box>
              {loading || busy ? (
                <CircularProgress size={20} sx={{ color: '#00FFD4', mr: 1 }} />
              ) : (
                <Switch
                  checked={enabled}
                  onChange={handleToggle}
                  disabled={blocked}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#00FFD4' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#00FFD4',
                    },
                  }}
                />
              )}
            </Box>

            {enabled && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                <Button
                  onClick={handleTest}
                  disabled={testing}
                  startIcon={testing ? <CircularProgress size={16} sx={{ color: '#00FFD4' }} /> : <SendIcon />}
                  sx={{
                    color: '#00FFD4',
                    textTransform: 'none',
                    border: '1px solid rgba(0, 255, 212, 0.4)',
                    '&:hover': { backgroundColor: 'rgba(0, 255, 212, 0.08)' },
                  }}
                >
                  Send a test notification
                </Button>
                <Button
                  onClick={handleLocalTest}
                  sx={{
                    color: '#8892A0',
                    textTransform: 'none',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                  }}
                >
                  Local test (no server)
                </Button>
              </Box>
            )}

            {blocked && (
              <Typography sx={{ color: '#FF3366', fontSize: '0.8rem', mt: 1.5 }}>
                Notifications are blocked in your browser. Allow them for this site in your
                browser settings, then toggle this on.
              </Typography>
            )}

            {isIos && (
              <>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
                <Typography sx={{ color: '#8892A0', fontSize: '0.8rem' }}>
                  On iPhone/iPad, notifications only work when RoundMaster is added to your
                  Home Screen and opened from there.
                </Typography>
              </>
            )}
          </>
        )}
      </CardContent>
    </GlassCard>
  );
}

export default PushNotificationSettings;
